/**
 * "Manage subscription" — opens the Stripe Billing Portal in a new tab.
 *
 * The Portal owns plan switch, cancellation, payment-method update, and
 * invoice history. We don't reimplement any of it — this button is the
 * one entry point. New tab so the user doesn't lose their workbench
 * context; on return, ``OrgCreditRing`` / ``BillingBudgetBanner`` refetch
 * entitlement via the visibility-change handler in ``billingFocusRefetch``.
 *
 * Visibility:
 *   * Server already gates ``/auth/billing/portal`` to OWNER + ADMIN. The
 *     button defers to whatever ``allowed`` the parent computed (typically
 *     the same RBAC predicate ``OrgCreditRing`` uses).
 *   * Hidden when the org has no Stripe customer yet (e.g. PayG-zero-wallet
 *     after cancel, or pre-checkout). The Portal call would fail with
 *     ``NoStripeCustomer``; we catch that error code and hide the button
 *     after the first failed click. The first render shows it optimistically
 *     because there's no cheap way to know up front (entitlement endpoint
 *     does NOT expose ``StripeCustomerId`` — that would be a small API
 *     addition; keeping that out of scope for v1).
 */
import React, { useCallback, useState } from 'react';
import { openBillingPortal, isBillingUiEnabled } from '@/services/stripeBilling';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';

const BTN_BASE =
  'px-2.5 py-1 text-xs font-medium rounded-md transition-colors ' +
  'border border-slate-300 text-slate-700 hover:bg-slate-50 ' +
  'disabled:opacity-50 disabled:cursor-wait';

export default function ManageSubscriptionButton({
  allowed = true,
  returnPath = '/billing',
  className = '',
  label = 'Manage subscription',
}) {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = await openBillingPortal({ returnPath });
      // ``noopener,noreferrer`` so Stripe's tab can't reach back into ours
      // via ``window.opener``. Standard hardening for any third-party tab
      // we open with user data context.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // ``NoStripeCustomer`` is a UX state, not an error to nag about —
      // the org never bought a subscription. Hide the button silently.
      if (err && err.code === 'NoStripeCustomer') {
        setHidden(true);
      } else {
        showToast(
          err?.message || 'Could not open billing portal — try again in a moment.',
          'error',
        );
      }
    } finally {
      setBusy(false);
    }
  }, [busy, returnPath, showToast]);

  // Billing UI master switch (single-account/no-Stripe deploys set it false).
  if (!isBillingUiEnabled() || !allowed || hidden) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`${BTN_BASE} ${className}`.trim()}
      aria-label={label}
    >
      {busy ? 'Opening…' : label}
    </button>
  );
}
